import { IUpdateTag, Tag, TagGroup } from 'contensis-management-api';
import mapErrorToObject from './mapAppErrorToObject';
import { MigrateStatus } from '~/models';
import ContensisRepository from '~/repositories/ContensisRepository';
import { compareFields } from '~/util/EntryHelper';

/**
 * Build a complete tag specifically for the target repository, providing
 * context of source and target repositories we can validate any field in the
 * given source tag for any required dependencies
 *
 * Output the tag as JSON for loading into the Management API using the `toJSON` method
 */
export class TagBuilder {
  contensis: ContensisRepository;
  origin: ContensisRepository;
  tagGroup?: TagGroup;
  finalValue?: IUpdateTag;

  existing?: Tag;
  source: Tag;
  status!: MigrateStatus;
  error?: MappedError;
  diff?: string;

  private overrideId?: string; // If we need to override the default transformed id

  /** Indicates the group does not exist but will be created as part of this migration */
  get builtGroup(): TagGroup | undefined {
    return this.contensis.tags.migrateGroups
      .find((mg) => mg.id === this.source.groupId)
      ?.toJSON() as TagGroup;
  }
  /** Tags supplied in the payload do not require an id
   * so we can generate a deterministic one for previews
   * and repeating migrations */
  get generateId() {
    return this.contensis.generateGuid(
      `${this.source.groupId}-${
        this.source.value ||
        Object.values(this.source.label || {})
          .map((label) => label)
          .join('-')
      }`
    );
  }
  get id() {
    return (
      this.overrideId ||
      this.existing?.id ||
      this.contensis.transformGuid(this.originalId)
    );
  }
  set id(val) {
    this.overrideId = val;
  }
  get languages() {
    return Object.keys(this.source.label || {}).filter((language) =>
      this.contensis.project.supportedLanguages.includes(language)
    );
  }
  get originalId() {
    return this.source.id;
  }

  constructor(
    contensis: ContensisRepository,
    origin: ContensisRepository,
    source: Tag
  ) {
    this.contensis = contensis;
    this.origin = origin;
    this.source = source;
    this.init();
  }
  init = () => {
    const { contensis } = this;
    // Generate a deterministic guid for source tags provided without one
    if (!this.source.id) this.source.id = this.generateId;

    // Find existing tag in target
    // match by (transformed) id or by value and groupId
    // We may have set existing from outside
    if (!this.existing) {
      this.existing = contensis.tags.raw?.find(
        (t) =>
          t.id === this.id ||
          (t.value === this.source.value && t.groupId === this.source.groupId)
      );
    }

    // Find the tag group in target
    this.tagGroup =
      contensis.context.tagGroups.find((g) => g.id === this.source.groupId) ||
      this.builtGroup;
  };

  /**
   * @returns tag as JSON
   */
  toJSON = (opts = {}) =>
    ({ ...(this.existing || {}), ...(this.finalValue || {}) }) as IUpdateTag;

  /**
   * Check we have the tag group and languages available to process this tag
   */
  validate = () => {
    const tagFriendlyName =
      this.source.value || Object.values(this.source.label || {})?.[0];

    if (!this.source.label || !Object.values(this.source.label || {})?.[0]) {
      this.error = mapErrorToObject(
        new Error(
          `A tag requires at least one localized label in the format '{ "en-GB": "Label" }'`
        )
      );
      return;
    }
    if (typeof this.source.value !== 'string') {
      this.error = mapErrorToObject(
        new Error(`Tag '${tagFriendlyName}' requires a valid value`)
      );
      return;
    }
    if (!this.tagGroup) {
      this.error = mapErrorToObject(
        new Error(
          `Tag group '${this.source.groupId}' from tag '${tagFriendlyName}' does not exist in project '${this.contensis.project.id}'`
        )
      );
    }

    if (!this.languages.length)
      this.error = mapErrorToObject(
        new Error(
          `Tag '${tagFriendlyName}' does not contain a label for any language supported by the target project '${this.contensis.project.id}' (${this.contensis.project.supportedLanguages.join(',')})`
        )
      );

    if (!this.source.groupId) {
      this.error = mapErrorToObject(
        new Error(`Tag '${tagFriendlyName}' does not contain a groupId`)
      );
    }
  };

  /**
   * Build the tag for the target taking the values from the source tag
   * Iterate all validated languages when building the localised label
   */
  build = () => {
    try {
      // Build the tag and set the migrate status
      this.validate();
      this.finalValue = {
        groupId: this.tagGroup?.id as string,
        id: this.id,
        value: this.source.value,
        label: Object.fromEntries(
          this.languages.map((language) => [
            language,
            this.source.label[language],
          ])
        ),
      };

      this.status = this.checkStatus();
    } catch (ex: any) {
      this.status = 'error';
      this.error = mapErrorToObject(ex);
      this.error.message = `Error building tag ${this.originalId}: ${this.error.message}`;
    }
  };

  /**
   * Check the tag for update status
   */
  checkStatus = (): MigrateStatus => {
    const { contensis, error, existing, nullFields } = this;

    if (error) return 'error';

    if (existing) {
      // Has the tag changed from the existing?
      const tag = this.toJSON();

      const { changed, diff } = compareFields(
        nullFields(tag),
        nullFields(existing),
        this.origin,
        contensis
      );

      if (changed) {
        this.diff = diff;
        return 'update';
      } else {
        return 'no change';
      }
    }

    return 'create';
  };

  /**
   * Set Contensis controlled fields to null before doing a diff as these will always be different
   */
  nullFields = (tag: IUpdateTag) =>
    ({
      ...tag,
      id: null,
      usageCount: null,
      version: null,
    }) as Tag & { id: null };
}
